import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PlusIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { nutritionAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'

/**
 * VH-165 — log a favourite in one tap, from a phone.
 *
 * The button's job is LOGGING, not managing. The existing FavoriteMealsModal is
 * a management surface — tabs, search, categories — and reaching a one-tap log
 * through it costs several taps. That modal stays, reachable from "Manage".
 *
 * Headless UI's Dialog rather than a hand-rolled overlay, matching QuickLogSheet
 * (VH-133): focus trapping, restore on close, Escape, and marking the rest of
 * the page inert are easy to get wrong and invisible when wrong.
 *
 * The query key is shared with FavoriteMealsWidget on purpose. Both trees mount
 * — the mobile/desktop split is CSS — and a shared key means TanStack serves the
 * second consumer from cache rather than firing a second request.
 */
export default function FavoritesQuickPickSheet({ open, onClose, selectedDate, onMealAdded, onManage }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['favoriteMeals'],
    queryFn: () => nutritionAPI.getFavoriteMeals({ limit: 5 }),
    select: (response) => response.data,
    enabled: open,
  })

  const favorites = data?.favorite_meals || []

  const addMutation = useMutation({
    mutationFn: (favoriteId) => nutritionAPI.addFavoriteToToday(favoriteId, null, selectedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meals'] })
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
      onMealAdded?.()
      onClose()
    },
  })

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-x-0 bottom-0 z-50">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="translate-y-full" enterTo="translate-y-0"
            leave="ease-in duration-150" leaveFrom="translate-y-0" leaveTo="translate-y-full"
          >
            {/* max-h + flex column so the list scrolls and the header stays put.
                Never h-full on the list — that is the clipped-scroll fault
                behind VH-158 and VH-166. */}
            <Dialog.Panel className="bg-surface-container rounded-t-2xl shadow-xl max-h-[70vh] flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <Dialog.Title className="font-semibold text-gray-900">
                  {t('favorites.title', 'Favorites')}
                </Dialog.Title>
                <button
                  type="button"
                  onClick={() => { onClose(); onManage?.() }}
                  className="text-sm text-primary-600 hover:text-primary-800"
                  data-testid="favourites-manage"
                >
                  {t('favorites.manage', 'Manage')} ›
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-2" data-testid="favourites-list">
                {isLoading && (
                  <p className="p-4 text-sm text-gray-500">{t('common.loading')}</p>
                )}

                {error && (
                  <p className="p-4 text-sm text-red-600">
                    {apiErrorMessage(error, t('favorites.loadError', 'Failed to load favorites'))}
                  </p>
                )}

                {!isLoading && !error && favorites.length === 0 && (
                  <p className="p-4 text-sm text-gray-500" data-testid="favourites-empty">
                    {t('favorites.emptyHint', 'Save a meal as a favourite to log it again in one tap.')}
                  </p>
                )}

                {favorites.map((favorite) => (
                  <button
                    key={favorite.id}
                    type="button"
                    onClick={() => addMutation.mutate(favorite.id)}
                    disabled={addMutation.isPending}
                    className="w-full flex items-center justify-between px-3 py-3 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                    data-testid={`favourite-${favorite.id}`}
                  >
                    <span>
                      <span className="block font-medium text-gray-900">{favorite.name}</span>
                      {favorite.total_calories != null && (
                        <span className="block text-xs text-gray-500">
                          {Math.round(favorite.total_calories)} cal
                        </span>
                      )}
                    </span>
                    {addMutation.isPending && addMutation.variables === favorite.id ? (
                      <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
                    ) : (
                      <PlusIcon className="h-5 w-5 text-primary-600" />
                    )}
                  </button>
                ))}

                {addMutation.isError && (
                  <p className="px-3 py-2 text-sm text-red-600" data-testid="favourites-add-error">
                    {apiErrorMessage(addMutation.error, t('common.error', 'An error occurred'))}
                  </p>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
